import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Divider,
  Grid,
  Button,
  IconButton,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Edit, Save, Cancel, Print } from "@mui/icons-material";
import DynamicQuantityInput from "../PackAndBottleInputMUI";
import {
  clearSelectedArticle,
  setSelectedArticle,
} from "../../store/articleSlice";
import {
  setInputValue,
  setQuantity,
  selectQuantity,
} from "../../store/dynamicQteInmputSlice";
import { fetchSpecificSales, fetchRelatedOrders, createOrder, clearSelectedSale, clearAlerts } from "../../store/salesSlice";
import { DocumentIdName } from "../../services/Enums";
import { formatQuantity, generateNumeroDoc, printSale } from "../../services/utils";
import MySelect from "../MySelect";
import { clearSelectedShop, fetchShops, setSelectedShop } from "../../store/shopSlice";
import { showSnackbar } from "../../store/snackbarSlice";

const SaleOrder = ({ closeOrder }) => {
  const dispatch = useDispatch();
  const { selectedSale, message, error, relatedOrders } = useSelector((state) => state.sale) || {};
  const { quantity } = useSelector(selectQuantity);
  const { shops } = useSelector((state) => state.shop);

  const [articleShops, setArticleShops] = useState({}); // Stores shop selection for each article
  const [selectedGlobalShop, setSelectedGlobalShop] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editedArticles, setEditedArticles] = useState(
    selectedSale?.articles.map((article) => ({
      ...article,
      toDelivery: article.toDelivery || article.quantity - article.calculatedDelivery || article.quantity,
      calculatedDelivery: article.calculatedDelivery ?? 0,
    })) || []
  );

  const handleShopChangeForArticle = (articleId, shopId) => {
    setArticleShops(prev => ({
      ...prev,
      [articleId]: shopId
    }));
  };
  const isArticleFullyDelivered = (article) => {
    return article.calculatedDelivery >= article.quantity;
  };
  const deliveredColor = '#e8f5e9'; // Vert clair pour livré
  const notDeliveredColor = '#ffebee'; // Rouge clair pour non livré
  const hoverDeliveredColor = '#c8e6c9'; // Vert plus foncé au survol
  const hoverNotDeliveredColor = '#ffcdd2'; // Rouge plus foncé au survol
  const applyGlobalShopToAll = () => {
    if (!selectedGlobalShop) {
      alert("Veuillez sélectionner un magasin global d'abord.");
      return;
    }

    const updatedShops = {};
    editedArticles.forEach(article => {
      updatedShops[article.article._id] = selectedGlobalShop;
    });

    setArticleShops(updatedShops);
  };


  useEffect(() => {
    dispatch(fetchShops({ page: 1, limit: 50 }));
    dispatch(fetchRelatedOrders({ page: 1, limit: 50, relatedOrder: selectedSale?._id }));

    return () => {
      dispatch(clearSelectedSale());
      dispatch(clearSelectedArticle());
      dispatch(clearSelectedShop());
      dispatch(clearAlerts());
    };
  }, [dispatch]);

  useEffect(() => {
    if (message || error) {
      const type = message ? 'success' : 'error';
      const content = message || error;
      dispatch(showSnackbar({ message: content, severity: type }));
      dispatch(clearAlerts());
    }
  }, [message, error]);

  useEffect(() => {
    if (selectedSale) {
      setEditedArticles(
        selectedSale.articles.map((article) => ({
          ...article,
          toDelivery: article.toDelivery || article.quantity - article.calculatedDelivery || article.quantity,
          calculatedDelivery: article.calculatedDelivery ?? 0,
        }))
      );
    }
  }, [selectedSale]);

  const handleEdit = (index) => {
    const articleToEdit = editedArticles[index];
    dispatch(setQuantity(articleToEdit.toDelivery));
    dispatch(setInputValue(articleToEdit.toDelivery));
    dispatch(setSelectedArticle(articleToEdit.article));
    setEditingRow(index);
  };

  const handleSave = async (index) => {
    setEditedArticles((prev) =>
      prev.map((article, i) =>
        i === index ? { ...article, toDelivery: quantity } : article
      )
    );
    setEditingRow(null);
  };

  const handleCancel = () => setEditingRow(null);

  const createMultipleDeliveryOrders = (printable) => {
    // Group articles by shop
    const articlesByShop = {};

    editedArticles.forEach((article) => {
      const shopId = articleShops[article.article._id];
      const toDeliver = parseFloat(article.toDelivery || 0);
      const remainingQuantity = article.quantity - article.calculatedDelivery;

      // Validate quantity
      if (toDeliver > remainingQuantity) {
        alert(`Cannot exceed remaining quantity for ${article.article.name}`);
        return;
      }

      if (toDeliver > 0) {
        if (!shopId && selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT) {
          alert(`Please select a shop for article: ${article.article.name}`);
          return;
        }

        const targetShopId = selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT
          ? shopId
          : selectedSale.shop._id;

        if (!articlesByShop[targetShopId]) {
          articlesByShop[targetShopId] = [];
        }

        articlesByShop[targetShopId].push({
          ...article,
          quantity: toDeliver,
        });
      }
    });

    // Check if any articles to deliver
    if (Object.keys(articlesByShop).length === 0) {
      alert("No quantity to deliver has been set or no shop selected");
      return;
    }

    // Create an order for each shop
    const orders = Object.entries(articlesByShop).map(([shopId, articles]) => ({
      ...selectedSale,
      relatedOrder: selectedSale._id,
      numero: generateNumeroDoc(),
      status: "PENDING",
      mouvementType: selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT
        ? DocumentIdName.ORDRE_DE_LIVRAISON
        : DocumentIdName.LIVRAISON,
      id: -1,
      shop: shopId,
      articles: articles.map(article => ({
        ...article,
        quantity: article.toDelivery,
      })),
    }));

    // Dispatch all orders
    Promise.all(orders.map(order => dispatch(createOrder(order)).unwrap()))
      .then(() => {
        dispatch(fetchSpecificSales({ _id: selectedSale._id })).unwrap()
          .then(() => {
            dispatch(fetchRelatedOrders({ page: 1, limit: 50, relatedOrder: selectedSale?._id }));
            if (printable) {
              orders.forEach(order => {
                printSale(
                  order,
                  order.mouvementType === DocumentIdName.ORDRE_DE_LIVRAISON
                    ? "ORDRE DE LIVRAISON"
                    : "BON DE LIVRAISON"
                );
              });
            }
          });
      })
      .catch(error => {
        console.error("Error creating orders:", error);
      });
  };

  if (!selectedSale || Object.keys(selectedSale).length === 0) {
    return (
      <Box p={2}>
        <Typography variant="h6" align="center">
          No Sale selected. Please choose a sale to view the details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h4" gutterBottom>
        Details de la commande
      </Typography>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1">
              <strong>Numero:</strong> {selectedSale.numero}
            </Typography>
            <Typography variant="body1">
              <strong>Status:</strong> {selectedSale.status}
            </Typography>
            <Typography variant="body1">
              <strong>Shop:</strong> {selectedSale?.shop?.name}
            </Typography>
            <Typography variant="body1">
              <strong>Date:</strong> {new Date(selectedSale.date).toLocaleDateString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1">
              <strong>Client/Fournisseur:</strong> {selectedSale.clientFournisseur.name || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ marginBottom: 2 }} />

      <Typography variant="h5" gutterBottom>
        Articles
      </Typography>

      {selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT && (
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <MySelect
            label="Magasin Global"
            options={shops}
            value={selectedGlobalShop}
            onChange={(e) => setSelectedGlobalShop(e.target.value)}
            multiple={false}
            getOptionValue={(option) => option._id}
            getOptionLabel={(option) => option.name}
          />
          <Button variant="outlined" onClick={applyGlobalShopToAll}>
            Appliquer à tous les articles
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Article</TableCell>
              <TableCell align="right">Quantite</TableCell>
              <TableCell align="right">Quantite livrée</TableCell>
              <TableCell align="right">Reste a livrer</TableCell>
              {selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT && (
                <TableCell>Shop</TableCell>
              )}
              <TableCell>Livraison</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editedArticles.map((article, index) => {
              const remainingQuantity = article.quantity - article.calculatedDelivery;

              return (
                <TableRow key={article._id || index}
                  sx={{
                    backgroundColor: isArticleFullyDelivered(article) ? deliveredColor : notDeliveredColor,
                    '&:hover': {
                      backgroundColor: isArticleFullyDelivered(article) ? hoverDeliveredColor : hoverNotDeliveredColor
                    }
                  }}
                >
                  <TableCell>{article.article.name}</TableCell>
                  <TableCell align="right">
                    {formatQuantity({
                      isVenteDetail: article?.article?.isAlowSecondaryUnit,
                      quantity: article?.quantity,
                      nombreDePiece: article?.article?.unitsQte,
                      primaryUnit: article?.article?.primaryUnits,
                      secondaryUnit: article?.article?.secondaryUnits,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {formatQuantity({
                      isVenteDetail: article?.article?.isAlowSecondaryUnit,
                      quantity: article?.calculatedDelivery,
                      nombreDePiece: article?.article?.unitsQte,
                      primaryUnit: article?.article?.primaryUnits,
                      secondaryUnit: article?.article?.secondaryUnits,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {formatQuantity({
                      isVenteDetail: article?.article?.isAlowSecondaryUnit,
                      quantity: remainingQuantity,
                      nombreDePiece: article?.article?.unitsQte,
                      primaryUnit: article?.article?.primaryUnits,
                      secondaryUnit: article?.article?.secondaryUnits,
                    })}
                  </TableCell>
                  {selectedSale.mouvementType === DocumentIdName.COMMANDE_CLIENT && (
                    <TableCell>
                      <MySelect
                        label="Shop"
                        options={shops}
                        value={articleShops[article.article._id] || ""}
                        onChange={(e) => handleShopChangeForArticle(article.article._id, e.target.value)}
                        multiple={false}
                        getOptionValue={(option) => option._id}
                        getOptionLabel={(option) => option.name}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {editingRow === index ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <DynamicQuantityInput />
                        <IconButton onClick={() => handleSave(index)} color="primary">
                          <Save />
                        </IconButton>
                        <IconButton onClick={handleCancel} color="secondary">
                          <Cancel />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        {formatQuantity({
                          isVenteDetail: article?.article?.isAlowSecondaryUnit,
                          quantity: article?.toDelivery,
                          nombreDePiece: article?.article?.unitsQte,
                          primaryUnit: article?.article?.primaryUnits,
                          secondaryUnit: article?.article?.secondaryUnits,
                        })}
                        <IconButton onClick={() => handleEdit(index)} color="primary">
                          <Edit />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => createMultipleDeliveryOrders(false)}
        >
          Creer un bon de livraison
        </Button>
        <br />
        <br />
        <Button
          variant="contained"
          color="primary"
          onClick={() => createMultipleDeliveryOrders(true)}
        >
          Creer et imprimer
        </Button>
      </div>

      <Box mt={3}>
        <Typography variant="h6">
          <strong>Repartition</strong>
        </Typography>
      </Box>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ border: "1px solid black", padding: "8px" }}>Numéro</th>
            <th style={{ border: "1px solid black", padding: "8px" }}>Status</th>
            <th style={{ border: "1px solid black", padding: "8px" }}>Shop</th>
            <th style={{ border: "1px solid black", padding: "8px" }}>Article</th>
            <th style={{ border: "1px solid black", padding: "8px" }}>Quantity</th>
            <th style={{ border: "1px solid black", padding: "8px" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {relatedOrders?.map((link, index) => {
            return (
              link.articles.map((article, idx) => (
                <tr key={`${index}-${idx}`}>
                  {idx === 0 && (
                    <>
                      <td
                        rowSpan={link.articles.length}
                        style={{ border: "1px solid black", padding: "8px", verticalAlign: "top" }}
                      >
                        {link.numero}
                      </td>
                      <td
                        rowSpan={link.articles.length}
                        style={{ border: "1px solid black", padding: "8px", verticalAlign: "top" }}
                      >
                        {link.status}
                      </td>
                      <td
                        rowSpan={link.articles.length}
                        style={{ border: "1px solid black", padding: "8px", verticalAlign: "top" }}
                      >
                        {link.shop.name}
                      </td>
                    </>
                  )}
                  <td style={{ border: "1px solid black", padding: "8px" }}>{article.article.name}</td>
                  <td style={{ border: "1px solid black", padding: "8px", textAlign: "center" }}>
                    {formatQuantity({
                      isVenteDetail: article?.article?.isAlowSecondaryUnit,
                      quantity: article?.quantity,
                      nombreDePiece: article?.article?.unitsQte,
                      primaryUnit: article?.article?.primaryUnits,
                      secondaryUnit: article?.article?.secondaryUnits,
                    })}
                  </td>
                  {idx === 0 && (
                    <td
                      rowSpan={link.articles.length}
                      style={{ border: "1px solid black", padding: "8px", verticalAlign: "top", textAlign: "center" }}
                    >
                      <button onClick={() => {
                        printSale(
                          link,
                          link.mouvementType === DocumentIdName.ORDRE_DE_LIVRAISON
                            ? "ORDRE DE LIVRAISON"
                            : "BON DE LIVRAISON"
                        );
                      }} style={{ padding: "5px 10px", cursor: "pointer" }}>
                        Print
                      </button>
                    </td>
                  )}
                </tr>
              ))
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default SaleOrder;